import '@/legacy-src/styles/style.scss'

import { GoogleTagManager } from '@next/third-parties/google'
import { VercelToolbar } from '@vercel/toolbar/next'
import dayjs from 'dayjs'
import updateLocale from 'dayjs/plugin/updateLocale'
import { getTranslate } from 'legacy-src/utilities/i18n'
import type { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'

import FbclidCapture from '@/legacy-src/analytics/FbclidCapture'
import { FbEventName, sendFbEventByName } from '@/legacy-src/analytics/fbPixelData'
import { GTM_ID, pageview } from '@/legacy-src/analytics/gtm'
import SalesDoubler from '@/legacy-src/analytics/SalesDoubler'
import Auth, { type AuthType } from '@/legacy-src/components/Auth'
import FetchCommonIntoRedux from '@/legacy-src/components/FetchCommonIntoRedux'
import HeadCommonTags from '@/legacy-src/components/head/HeadCommon'
import ProvidersAuthenticated from '@/legacy-src/components/ProvidersAuthenticated'
import ProvidersCommon from '@/legacy-src/components/ProvidersCommon'
import ProvidersCommonPageRouter from '@/legacy-src/components/ProvidersCommonPageRouter'
import AskForReview from '@/legacy-src/components/userActivity/AskForReview'
import { useUserIp } from '@/legacy-src/contexts/user-ip-context'
import useIsAppWebView from '@/legacy-src/hooks/useIsAppWebView'
import useScrollPosition from '@/legacy-src/hooks/useScrollPosition'
import { rebrandFontVariables } from '@/legacy-src/rebrand/fonts'
import { withReduxAndAxios } from '@/legacy-src/services/withReduxAndAxios'
import { createApis } from '@/legacy-src/store/createApis'
import { createStore } from '@/legacy-src/store/createStore'
import { type PageCtxType } from '@/legacy-src/types/NextJsTypes'

import type { NextPageWithLayout } from '@/src/types/next'

import '@hedepy/common/schemas/extendZodWithDummyOpenApi'

type WrappedAppProps = AppProps<{ auth?: AuthType }> & {
  Component: NextPageWithLayout<Record<string, unknown>>
} & PageCtxType & {
    store: any
    setToken: any
    apis: any
  }

function MyApp({ Component, pageProps, store, setToken, apis }: WrappedAppProps) {
  const router = useRouter()
  const { t } = getTranslate()
  const isWebView = useIsAppWebView()
  const user = store.getState().user
  const { userIp } = useUserIp()
  useScrollPosition()

  // const shouldInjectToolbar = process.env.NODE_ENV === 'development'
  const shouldInjectToolbar = false

  useEffect(() => {
    const handlePageView = (url) => {
      pageview(url, { id: user.id })
      sendFbEventByName({
        eventName: FbEventName.PageView,
        user: { ...user, userIp },
        customData: {
          url,
        },
      })
    }
    router.events.on('routeChangeComplete', handlePageView)

    return () => {
      router.events.off('routeChangeComplete', handlePageView)
    }
  }, [router.events, user.id])

  useEffect(() => {
    dayjs.locale(process.env.NEXT_PUBLIC_LOCALE)
    dayjs.extend(updateLocale)
    dayjs.updateLocale('en', {
      weekStart: 1,
    })
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      sendFbEventByName({ eventName: 'timer_25sec', user: { ...user, userIp } })
    }, 25000)

    return () => clearTimeout(timer)
  }, [])

  const getLayout = Component.getLayout || ((page: React.ReactNode) => page)

  const renderPage = () => getLayout(<Component {...pageProps} apis={apis} setToken={setToken} t={t} />)

  return (
    <>
      <HeadCommonTags />

      {isWebView ? (
        <style>{`#CybotCookiebotDialog{display:none!important;}`}</style>
      ) : (
        <div className="cookiebot-overlay" />
      )}
      <div className={`app-container ${isWebView ? 'is-web-view' : ''} ${rebrandFontVariables}`}>
        <ProvidersCommonPageRouter {...{ store, setToken, apis }}>
          <ProvidersCommon>
            <FetchCommonIntoRedux>
              {Component.auth ? (
                <Auth auth={Component.auth}>
                  <ProvidersAuthenticated>
                    <AskForReview />

                    {renderPage()}
                  </ProvidersAuthenticated>
                </Auth>
              ) : (
                renderPage()
              )}
              {shouldInjectToolbar && <VercelToolbar />}
            </FetchCommonIntoRedux>
          </ProvidersCommon>
        </ProvidersCommonPageRouter>
      </div>

      <GoogleTagManager gtmId={GTM_ID} />
      <SalesDoubler />
      <FbclidCapture />
    </>
  )
}

export default withReduxAndAxios(createStore, createApis)(MyApp)
